<template>
    <div class="page-container">

        <CrmPageHeader
            title="Credenciales de Integración"
            subtitle="Ciencuadras vía API Conecta (Seguros Bolívar)"
            icon="fas fa-plug"
        >
            <span class="count-pill">
                <i class="fas fa-plug"></i>
                {{ credentials.length }} credenciales
            </span>
            <soft-button variant="gradient" size="sm" @click="openCreate()">
                <i class="fas fa-plus me-1"></i>
                Nueva credencial
            </soft-button>
            <soft-button variant="outline" color="secondary" size="sm"
                @click="fetchCredentials()" :loading="spinner">
                <i class="fas fa-sync-alt me-1"></i>
                Actualizar
            </soft-button>
        </CrmPageHeader>

        <!-- Alert -->
        <soft-alert v-if="alertTable.state" :color="alertTable.color" class="mb-4" dismissible>
            {{ alertTable.message }}
        </soft-alert>

        <!-- Table card -->
        <div class="crm-card">
            <div class="crm-card__header d-flex align-items-center justify-content-between">
                <div>
                    <p style="font-size:.82rem;font-weight:600;color:#1E293B;margin:0">Credenciales registradas</p>
                    <p style="font-size:.72rem;color:#94A3B8;margin:0">Gestiona las conexiones con proveedores externos</p>
                </div>
            </div>
            <div class="crm-card__body p-0">
                <div class="table-responsive">
                    <soft-spinner v-if="spinner" />
                    <table v-else class="crm-table">
                        <thead>
                            <tr>
                                <th>Aliado</th>
                                <th>Client ID</th>
                                <th>Admin destino</th>
                                <th class="text-center">Activo</th>
                                <th>Última sync</th>
                                <th class="text-center">Estado</th>
                                <th class="text-center">Inmuebles</th>
                                <th class="text-end">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="cred in credentials" :key="cred.id">
                                <td>
                                    <p class="partner-name">{{ cred.partner_name }}</p>
                                    <span class="provider-tag">{{ cred.provider }}</span>
                                </td>
                                <td>
                                    <code class="client-id-pill" :title="cred.client_id">
                                        {{ truncate(cred.client_id) }}
                                    </code>
                                </td>
                                <td>
                                    <span style="font-size:.8rem;color:#475569">{{ cred.assignedUser?.email || '—' }}</span>
                                </td>
                                <td class="text-center">
                                    <div class="form-check form-switch d-inline-flex m-0">
                                        <input
                                            class="form-check-input"
                                            type="checkbox"
                                            role="switch"
                                            :checked="cred.active"
                                            :disabled="rowBusy === cred.id"
                                            @change="toggleActive(cred)"
                                        />
                                    </div>
                                </td>
                                <td>
                                    <span class="sync-time" :title="cred.last_synced_at || ''">
                                        {{ relativeTime(cred.last_synced_at) }}
                                    </span>
                                </td>
                                <td class="text-center">
                                    <span class="sync-badge" :class="statusBadgeClass(cred.last_sync_status)" :title="cred.last_error || ''">
                                        {{ statusLabel(cred.last_sync_status) }}
                                    </span>
                                </td>
                                <td class="text-center">
                                    <span class="sync-count">
                                        <strong>{{ cred.total_synced ?? 0 }}</strong>
                                        <span class="sync-count-sep"> / {{ cred.total_external ?? 0 }}</span>
                                    </span>
                                </td>
                                <td class="text-end">
                                    <div class="d-inline-flex gap-1">
                                        <button
                                            class="tbl-btn"
                                            title="Probar conexión"
                                            :disabled="rowBusy === cred.id"
                                            @click="testCredential(cred)"
                                        >
                                            <i class="fas fa-vial"></i>
                                        </button>
                                        <button
                                            class="tbl-btn"
                                            title="Sincronizar ahora"
                                            :disabled="rowBusy === cred.id"
                                            @click="syncCredential(cred)"
                                        >
                                            <i class="fas fa-sync-alt"></i>
                                        </button>
                                        <button
                                            class="tbl-btn"
                                            title="Editar"
                                            :disabled="rowBusy === cred.id"
                                            @click="openEdit(cred)"
                                        >
                                            <i class="fas fa-edit"></i>
                                        </button>
                                        <button
                                            class="tbl-btn tbl-btn--danger"
                                            title="Eliminar"
                                            :disabled="rowBusy === cred.id"
                                            @click="deleteCredential(cred)"
                                        >
                                            <i class="fas fa-trash"></i>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                            <tr v-if="credentials.length === 0">
                                <td colspan="8" class="text-center py-5">
                                    <i class="fas fa-inbox d-block mb-2" style="font-size:2rem;color:#CBD5E1"></i>
                                    <span style="font-size:.82rem;color:#94A3B8">No hay credenciales registradas.</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Form Modal -->
        <soft-modal @close="closeForm" :modalActive="showForm" v-if="showForm" size="lg">
            <template #header>
                <div style="display:flex;align-items:center;gap:10px">
                    <div style="width:32px;height:32px;border-radius:8px;background:linear-gradient(135deg,#1E3A5F,#2563EB);display:flex;align-items:center;justify-content:center;color:#fff;font-size:.85rem">
                        <i class="fas fa-plug"></i>
                    </div>
                    <span>{{ editingCredential ? 'Editar credencial' : 'Nueva credencial' }}</span>
                </div>
            </template>
            <credential-form-modal
                :credential="editingCredential"
                :admins="admins"
                :saving="isLoading"
                :loading-admins="adminsLoading"
                @save="saveCredential"
                @cancel="closeForm"
                @search="searchAdmins"
            />
        </soft-modal>

        <!-- Loading overlay -->
        <div v-if="isLoading" class="loading-overlay">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Cargando...</span>
            </div>
        </div>
    </div>
</template>

<script lang="js">
import SoftAlert from '@/components/SoftAlert.vue';
import SoftButton from '@/components/SoftButton.vue';
import SoftModal from '@/components/SoftModal.vue';
import SoftSpinner from '@/components/SoftSpinner.vue';
import CrmPageHeader from '@/components/CrmPageHeader.vue';
import CredentialFormModal from './components/CredentialFormModal.vue';
import { useExternalCredentials } from './useExternalCredentials.js';

export default {
    name: 'ExternalCredentials',
    components: { SoftAlert, SoftButton, SoftModal, SoftSpinner, CrmPageHeader, CredentialFormModal },
    setup() {
        const {
            spinner, isLoading, rowBusy, adminsLoading, alertTable,
            showForm, editingCredential,
            credentials, admins,
            fetchCredentials, fetchAdmins, searchAdmins,
            openCreate, openEdit, closeForm, saveCredential,
            deleteCredential, testCredential, syncCredential, toggleActive,
        } = useExternalCredentials();

        fetchCredentials();
        fetchAdmins();

        const truncate = (value, len = 14) => {
            if (!value) return '—';
            return value.length > len ? `${value.slice(0, len)}…` : value;
        };

        const statusLabel = (status) => {
            const map = { success: 'Exitoso', partial: 'Parcial', failed: 'Fallido' };
            return map[status] || 'Sin sync';
        };

        const statusBadgeClass = (status) => {
            const map = {
                success: 'sync-badge--success',
                partial: 'sync-badge--warning',
                failed:  'sync-badge--danger',
            };
            return map[status] || 'sync-badge--default';
        };

        const relativeTime = (value) => {
            if (!value) return 'Nunca';
            const date = new Date(value);
            if (isNaN(date.getTime())) return value;
            const diffMs = date.getTime() - Date.now();
            const absSec = Math.abs(diffMs) / 1000;
            const past = diffMs < 0;
            const fmt = (n, unit) => `${past ? 'hace' : 'en'} ${n} ${unit}${n === 1 ? '' : 's'}`;
            if (absSec < 60) return past ? 'hace instantes' : 'en instantes';
            if (absSec < 3600) return fmt(Math.round(absSec / 60), 'min');
            if (absSec < 86400) return fmt(Math.round(absSec / 3600), 'hora');
            if (absSec < 2592000) return fmt(Math.round(absSec / 86400), 'día');
            return date.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' });
        };

        return {
            spinner, isLoading, rowBusy, adminsLoading, alertTable,
            showForm, editingCredential,
            credentials, admins,
            fetchCredentials, searchAdmins,
            openCreate, openEdit, closeForm, saveCredential,
            deleteCredential, testCredential, syncCredential, toggleActive,
            truncate, statusLabel, statusBadgeClass, relativeTime,
        };
    }
};
</script>

<style scoped>
/* Count pill in header */
.count-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: #F1F5F9;
    border: 1px solid #E2E8F0;
    border-radius: 20px;
    font-size: .75rem;
    font-weight: 600;
    color: #475569;
}

/* Table */
.crm-table {
    width: 100%;
    border-collapse: collapse;
}
.crm-table th {
    padding: 10px 16px;
    font-size: .68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .07em;
    color: #64748B;
    background: #F8FAFC;
    border-bottom: 1px solid #E2E8F0;
    white-space: nowrap;
}
.crm-table td {
    padding: 12px 16px;
    border-bottom: 1px solid #F1F5F9;
    vertical-align: middle;
}
.crm-table tbody tr:last-child td { border-bottom: none; }
.crm-table tbody tr:hover td { background: #EFF6FF; }

/* Partner cell */
.partner-name { font-size: .82rem; font-weight: 600; color: #1E293B; margin: 0 0 2px; }
.provider-tag {
    font-size: .68rem;
    font-weight: 600;
    color: #64748B;
    background: #F1F5F9;
    padding: 1px 6px;
    border-radius: 4px;
}

/* Client ID pill */
.client-id-pill {
    display: inline-block;
    background: #EFF6FF;
    color: #1D4ED8;
    border: 1px solid #BFDBFE;
    padding: 3px 8px;
    border-radius: 6px;
    font-size: .72rem;
    font-family: 'Courier New', monospace;
    letter-spacing: .02em;
}

/* Sync time */
.sync-time { font-size: .78rem; color: #64748B; }

/* Sync status badges */
.sync-badge {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: .68rem;
    font-weight: 600;
}
.sync-badge--success { background: #ECFDF5; color: #059669; }
.sync-badge--warning { background: #FFFBEB; color: #D97706; }
.sync-badge--danger  { background: #FEF2F2; color: #DC2626; }
.sync-badge--default { background: #F1F5F9; color: #64748B; border: 1px solid #E2E8F0; }

/* Sync count */
.sync-count { font-size: .8rem; color: #1E293B; }
.sync-count-sep { color: #94A3B8; }

/* Table action buttons */
.tbl-btn {
    width: 30px;
    height: 30px;
    border: none;
    background: transparent;
    color: #94A3B8;
    border-radius: 6px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: .82rem;
    transition: all .15s ease;
}
.tbl-btn:hover:not(:disabled) { background: #EFF6FF; color: #2563EB; }
.tbl-btn--danger:hover:not(:disabled) { background: #FEF2F2; color: #DC2626; }
.tbl-btn:disabled { opacity: .4; cursor: not-allowed; }

/* Toggle switch - override Bootstrap to blue */
.form-check-input:checked {
    background-color: #2563EB;
    border-color: #2563EB;
}

/* Loading overlay */
.loading-overlay {
    position: fixed;
    inset: 0;
    background: rgba(255,255,255,0.7);
    z-index: 9999;
    display: flex;
    justify-content: center;
    align-items: center;
    backdrop-filter: blur(2px);
}
</style>
